import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Box } from '@chakra-ui/react';
import PageHeader from '../../components/PageHeader/PageHeader.tsx';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';
import OrganisationPageSkeleton from './OrganisationPageSkeleton.tsx';
import AleaEditOrganisationModal from '../../modals/AleaEditOrganisationModal/AleaEditOrganisationModal.tsx';
import { useDisclosure } from '@chakra-ui/react';
import AccountSettingsModal from '../../modals/AccountSettingsModal/AccountSettingsModal.tsx';
import InviteNewUserModal from '../../modals/InviteNewUserModal/InviteNewUserModal.tsx';
import AleaInfoModal from '../../modals/AleaInfoModal/AleaInfoModal.tsx';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import {
  getOrganisationCockpitUsersQueryKey,
  getOrganisationQueryKey,
  useOrganisationCockpitUsersQuery,
  useOrganisationQuery,
} from './organisationPage.queries.ts';
import { useOrganisationPageMutations } from './organisationPage.mutations.ts';
import OrganisationDetailsSection from './components/OrganisationDetailsSection.tsx';
import OrganisationCockpitUsersSection from './components/OrganisationCockpitUsersSection.tsx';

const OrganisationPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();

  // Modal controls
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Account settings modal
  const {
    isOpen: isAccountSettingsOpen,
    onOpen: onOpenAccountSettings,
    onClose: onCloseAccountSettings,
  } = useDisclosure();

  // Invite new user modal
  const { isOpen: isInviteOpen, onOpen: onOpenInvite, onClose: onCloseInvite } = useDisclosure();

  // State for row action menus & dialogs
  const [activeActionRowId, setActiveActionRowId] = useState<string | null>(null);

  // Single info modal for simple success messages (auto-closes)
  const [infoModalMessageKey, setInfoModalMessageKey] = useState<string | null>(null);

  // Show invite success info after modal closes
  const [shouldShowInviteInfoAfterClose, setShouldShowInviteInfoAfterClose] = useState<boolean>(false);

  // URL param overrides user's organisation id
  const targetOrganisationId = id || user?.organisation?.id;

  // React Query keys (centralized in a separate file)
  const organisationQueryKey = useMemo(() => getOrganisationQueryKey(targetOrganisationId), [targetOrganisationId]);
  const organisationCockpitUsersQueryKey = useMemo(
    () => getOrganisationCockpitUsersQueryKey(targetOrganisationId),
    [targetOrganisationId]
  );

  // Data (cached via React Query)
  const organisationQuery = useOrganisationQuery(targetOrganisationId);
  const cockpitUsersQuery = useOrganisationCockpitUsersQuery(targetOrganisationId);

  // CRUD actions are handled as React Query mutations (extracted to a separate file)
  const { resendInviteMutation, setOrganisationCache, invalidateOrganisationCockpitUsers, isCrudLoading } =
    useOrganisationPageMutations(
      { organisationQueryKey, organisationCockpitUsersQueryKey },
      {
        onShowResendInfo: () => setInfoModalMessageKey('invitationEmailHasBeenSent'),
      }
    );

  // Helper to close info modal manually
  const closeInfoModal = () => setInfoModalMessageKey(null);

  // Auto-close info modal after 2 seconds
  useEffect(() => {
    if (!infoModalMessageKey) return;
    const timer = setTimeout(() => closeInfoModal(), 2000);
    return () => clearTimeout(timer);
  }, [infoModalMessageKey]);

  // Show invite info only after the Add User modal has actually closed
  useEffect(() => {
    if (!isInviteOpen && shouldShowInviteInfoAfterClose) {
      setInfoModalMessageKey('invitationEmailHasBeenSent');
      setShouldShowInviteInfoAfterClose(false);
    }
  }, [isInviteOpen, shouldShowInviteInfoAfterClose]);

  // Add a function to check if user can edit
  const canEdit = () => {
    return user?.role === 'Organisation manager';
  };

  /**
   * Handles the edit action by opening the edit modal
   */
  const handleEdit = () => {
    onOpen();
  };

  // Page data
  const organisationData = organisationQuery.data;
  const cockpitUsers = cockpitUsersQuery.data ?? [];

  // Show skeleton while initial data is loading
  if (organisationQuery.isLoading || !organisationData) {
    return <OrganisationPageSkeleton />;
  }

  return (
    <Flex data-testid="organisation-page" flexFlow="column" borderRadius="20px">
      <PageHeader title={organisationData.name} canEdit={canEdit()} />

      <OrganisationDetailsSection
        title={t('masterData')}
        nameLabel={t('nameOfOrganization')}
        name={organisationData.name}
        address={organisationData.address}
        canEdit={canEdit()}
        onEdit={handleEdit}
      />

      <OrganisationCockpitUsersSection
        canEdit={canEdit()}
        cockpitUsers={cockpitUsers}
        activeActionRowId={activeActionRowId}
        onSetActiveActionRowId={setActiveActionRowId}
        currentUserId={user?.id}
        onOpenInvite={onOpenInvite}
        onOpenAccountSettings={onOpenAccountSettings}
        onResendInvite={(accountId) => resendInviteMutation.mutate(accountId)}
      />

      <AleaEditOrganisationModal
        isOpen={isOpen}
        onClose={onClose}
        organisation={organisationData}
        onSaved={(updated) => setOrganisationCache(updated)}
      />

      <AccountSettingsModal
        isOpen={isAccountSettingsOpen}
        onClose={onCloseAccountSettings}
        onSaved={() => {
          setInfoModalMessageKey('changesSaved');
          invalidateOrganisationCockpitUsers();
        }}
      />

      <InviteNewUserModal
        isOpen={isInviteOpen}
        onClose={onCloseInvite}
        role="Organisation manager"
        carehomeId={null}
        onConfirm={() => {
          invalidateOrganisationCockpitUsers();
          setShouldShowInviteInfoAfterClose(true);
        }}
      />

      <AleaInfoModal
        isOpen={!!infoModalMessageKey}
        onClose={closeInfoModal}
        message={infoModalMessageKey ? t(infoModalMessageKey) : ''}
      />

      {isCrudLoading && <AleaLoader />}

      {/* Backdrop when row action menu is open */}
      {activeActionRowId && (
        <Box
          position="fixed"
          top={0}
          left={0}
          w="100vw"
          h="100vh"
          bg="rgba(0, 0, 0, 0.20)"
          zIndex={1500}
          onClick={() => setActiveActionRowId(null)}
        />
      )}
    </Flex>
  );
};

export default OrganisationPage;
